import React, {
  createContext,
  useContext,
  useCallback,
  useMemo,
  useEffect,
} from "react";
import SpinningLoader from "../components/SpinningLoader";
import { usePortfolioEstimateAssets } from "../hooks/query/queries";
import { useQueryClient } from "@tanstack/react-query";
import { useAuth } from "./AuthContext";

const PortfolioEstimateContext = createContext();

export const PortfolioEstimateProvider = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const queryClient = useQueryClient();

  const {
    data: portfolioData,
    isLoading,
    error: queryError,
    dataUpdatedAt: lastFetched,
  } = usePortfolioEstimateAssets();

  const fetchPortfolioEstimate = useCallback(
    async (forceRefresh = false) => {
      if (forceRefresh) {
        await queryClient.invalidateQueries({
          queryKey: ["portfolioEstimateAssets"],
        });
      }
      const data = queryClient.getQueryData(["portfolioEstimateAssets"]);
      return data || portfolioData;
    },
    [queryClient, portfolioData]
  );

  const clearPortfolioData = useCallback(() => {
    queryClient.removeQueries({
      queryKey: ["portfolioEstimateAssets"],
    });
  }, [queryClient]);

  const getAssetsByType = useCallback(
    (type) => {
      if (!portfolioData?.assets) return [];
      return portfolioData.assets.filter(
        (asset) => asset.currencyType === type || asset.type === type
      );
    },
    [portfolioData]
  );

  const getAssetById = useCallback(
    (assetId) => {
      if (!portfolioData?.assets) return null;
      return (
        portfolioData.assets.find(
          (asset) => asset.assetId === assetId || asset.asset === assetId
        ) || null
      );
    },
    [portfolioData]
  );

  useEffect(() => {
    if (!isAuthenticated) {
      clearPortfolioData();
    }
  }, [isAuthenticated, clearPortfolioData]);

  const error = queryError?.message || (queryError ? String(queryError) : null);

  const value = useMemo(
    () => ({
      portfolioData,
      isLoading,
      error,
      lastFetched: lastFetched || null,
      fetchPortfolioEstimate,
      clearPortfolioData,
      getAssetsByType,
      getAssetById,
    }),
    [
      portfolioData,
      isLoading,
      error,
      lastFetched,
      fetchPortfolioEstimate,
      clearPortfolioData,
      getAssetsByType,
      getAssetById,
    ]
  );

  if (isLoading && !portfolioData) {
    return <SpinningLoader />;
  }

  return (
    <PortfolioEstimateContext.Provider value={value}>
      {children}
    </PortfolioEstimateContext.Provider>
  );
};

export const usePortfolioEstimate = () => {
  const context = useContext(PortfolioEstimateContext);
  if (!context) {
    throw new Error(
      "usePortfolioEstimate must be used within PortfolioEstimateProvider"
    );
  }
  return context;
};
